import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { 
  setTasks, 
  setError, 
  setLoading,
  updateTask,
  updateSubtask,
  setProjectStatus,
  setTeamAssigned,
  setTeamTaskAssigned,
  startTask
} from '../features/projectTasksSlice';

const API_URL = 'https://api-4h4srbodqa-uc.a.run.app';
// const API_URL = 'http://localhost:4000';
const userName = window.localStorage.getItem('projectUserDisplayName');

export const useProjectTasks = (projectId) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const invalidateProjectTasks = () => {
    queryClient.invalidateQueries({ queryKey: ['projectTasks', projectId] });
  };

  // Main query for fetching tasks
  const query = useQuery({
    queryKey: ['projectTasks', projectId],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_URL}/projects/${projectId}/tasks`);
        return response.data;
      } catch (error) {
        console.error('Fetch error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      dispatch(setTasks(data));
    },
    onError: (error) => {
      console.error('Fetch error:', error);
      dispatch(setError(error.message));
      dispatch(setLoading(false));
    },
    onSettled: () => {
      dispatch(setLoading(false));
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 3
  });

// Start task mutation
const startTaskMutation = useMutation({
  mutationFn: async (variables) => {
    // Prepare the request body
    const requestBody = {
      username: localStorage.getItem('projectUserDisplayName'),
      timestamp: variables.timestamp
    };

    // Add project manager data if provided
    if (variables.projectManagerId && variables.projectManagerName) {
      requestBody.projectManagerId = variables.projectManagerId;
      requestBody.projectManagerName = variables.projectManagerName;
    }

    const { data } = await axios.post(
      `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/start`,
      // `http://localhost:4000/projects/${projectId}/tasks/${variables.taskId}/start`,
      requestBody
    );
    return data;
  },
  onSuccess: (data, variables) => {
    dispatch(startTask(variables));
    invalidateProjectTasks();
    
    // Optional: Show success message based on whether PM was assigned
    if (data.assignedProjectManager) {
      console.log(`Task started with Project Manager: ${data.assignedProjectManager.name}`);
    } else {
      console.log('Task started successfully');
    }
  }
});

  // Complete task mutation
  const completeTaskMutation = useMutation({
    mutationFn: async (variables) => {
      const { data } = await axios.put(
        `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/complete`,
        // `http://127.0.0.1:4000/projects/${projectId}/tasks/${variables.taskId}/complete`,
        { username: localStorage.getItem('projectUserDisplayName') }
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.projectStatus) {
        dispatch(setProjectStatus(data.projectStatus));
      }
      invalidateProjectTasks();
    }
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: async (variables) => {
      const { data } = await axios.patch(
        `${API_URL}/projects/${projectId}/tasks/${variables.taskId}`,
        {
          description: variables.description,
          username: variables.username,
          qualityCheck: variables.qualityCheck || null,
          folder: variables.folder,
          files: variables.file // Send file URLs and metadata
        }
      );
      return data;
    },
    onMutate: async (variables) => {
      const previousTasks = queryClient.getQueryData(['projectTasks', projectId]);
  
      dispatch(updateTask({
        taskId: variables.taskId,
        updates: {
          description: variables.description,
          username: variables.username,
          folder: variables.folder,
          files: variables.file
        }
      }));
  
      return { previousTasks };
    },
    onError: (err, variables, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['projectTasks', projectId], context.previousTasks);
      }
      console.error('Update task error:', err);
    },
    onSettled: () => {
      queryClient.invalidateQueries(['projectTasks', projectId]);
    }
  });

  // Update subtask mutation
  // const updateSubtaskMutation = useMutation({
  //   mutationFn: async (variables) => {
  //     const formData = new FormData();
      
  //     formData.append('username', variables.username);
  //     formData.append('folder', variables.folder || 'General');
      
  //     Object.entries(variables.updateData).forEach(([key, value]) => {
  //       formData.append(key, value);
  //     });
      
  //     if (variables.file) {
  //       formData.append('file', variables.file);
  //     }
  
  //     const { data } = await axios.patch(
  //       `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/subtasks/${variables.subtaskId}`,
  //       formData,
  //       {
  //         headers: {
  //           'Content-Type': 'multipart/form-data'
  //         }
  //       }
  //     );
  //     return data;
  //   },
  //   onSuccess: (response, variables) => {
  //     dispatch(updateSubtask({
  //       taskId: variables.taskId,
  //       subtaskId: variables.subtaskId,
  //       updates: {
  //         ...variables.updateData,
  //         username: variables.username,
  //         folder: variables.folder,
  //         ...(response.fileDetails && { fileDetails: response.fileDetails })
  //       }
  //     }));
  //     invalidateProjectTasks();
  //   }
  // });
  // const updateSubtaskMutation = useMutation({
  //   mutationFn: async (variables) => {
  //     const { data } = await axios.patch(
  //       `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/subtasks/${variables.subtaskId}`,
  //       {
  //         description: variables.description,
  //         username: variables.username,
  //         folder: variables.folder,
  //         files: variables.files // Send file URLs and metadata
  //       }
  //     );
  //     return data;
  //   },
  
  //   onMutate: async (variables) => {
  //     // Cancel any outgoing refetches so they don't overwrite our optimistic update
  //     await queryClient.cancelQueries(['projectTasks', projectId]);
  
  //     // Snapshot the previous value
  //     const previousTasks = queryClient.getQueryData(['projectTasks', projectId]);
  
  //     // Optimistically update the subtask
  //     dispatch(updateSubtask({
  //       taskId: variables.taskId,
  //       subtaskId: variables.subtaskId,
  //       updates: {
  //         description: variables.description,
  //         username: variables.username,
  //         folder: variables.folder,
  //         files: variables.files,
  //         updatedAt: new Date().toISOString(),
  //         descriptions: variables.description ? [
  //           {
  //             text: variables.description,
  //             timestamp: new Date().toISOString(),
  //             username: variables.username
  //           },
  //           ...(previousTasks
  //             ?.find(t => t.taskId === variables.taskId)
  //             ?.subtasks
  //             ?.find(s => s.id === variables.subtaskId)
  //             ?.descriptions || [])
  //         ] : undefined
  //       }
  //     }));
  
  //     // Return a context object with the previous tasks
  //     return { previousTasks };
  //   },
  
  //   onError: (err, variables, context) => {
  //     // If the mutation fails, use the context returned from onMutate to roll back
  //     if (context?.previousTasks) {
  //       queryClient.setQueryData(['projectTasks', projectId], context.previousTasks);
  //     }
  //     console.error('Update subtask error:', err);
  //   },
  
  //   onSettled: () => {
  //     // Always refetch after error or success to ensure server state
  //     queryClient.invalidateQueries(['projectTasks', projectId]);
  //   }
  // });
  const updateSubtaskMutation = useMutation({
    mutationFn: async (variables) => {
      const { data } = await axios.patch(
        `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/subtasks/${variables.subtaskId}`,
        {
          description: variables.description,
          username: variables.username,
          folder: variables.folder || 'Genrel',
          files: variables.files, // Send file URLs and metadata
          completed: variables.completed || false,
          // name: variables.name || null
        }
      );
      return data;
    },
onMutate: async (variables) => {
      const previousTasks = queryClient.getQueryData(['projectSubTasks', projectId]);
  
      dispatch(updateSubtask({
        taskId: variables.taskId,
	subtaskId: variables.subtaskId,
        updates: {
          description: variables.description,
          username: variables.username,
          folder: variables.folder,
          files: variables.files,
          completed: variables.completed,
          // name: variables.name
        }
      }));
  
      return { previousTasks };
    },
 onError: (err, variables, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['projectSubtasks', projectId], context.previousTasks);
      }
      console.error('Update task error:', err);
    },
    onSettled: () => {
      queryClient.invalidateQueries(['projectSubtasks', projectId]);
    }
  });

  // Add subtask mutation
  // const addSubtaskMutation = useMutation({
  //   mutationFn: async (variables) => {
  //     const { data } = await axios.put(
  //       `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/subtasks`,
  //       {
  //         name: variables.name,
  //         username: variables.username
  //       }
  //     );
  //     return data;
  //   },
  //   onSettled: invalidateProjectTasks
  // });

  // Delete subtask mutation
  const deleteSubtaskMutation = useMutation({
    mutationFn: async (variables) => {
      const { data } = await axios.delete(
        `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/subtasks/${variables.subtaskId}`,
        {
          data: { username: userName }
        }
      );
      return data;
    },
    onSettled: invalidateProjectTasks
  });

  // Assign designer mutation
  const assignDesignerMutation = useMutation({
    mutationFn: async (variables) => {
      const { data } = await axios.post(
        `${API_URL}/projects/${projectId}/assign-designer`,
        variables
      );
      return data;
    },
    onSuccess: (data) => {
      dispatch(setProjectStatus('design-phase'));
      if (data.hasTeamAssigned !== undefined) {
        dispatch(setTeamAssigned(data.hasTeamAssigned));
      }
      invalidateProjectTasks();
    }
  });

  // Assign designer team mutation
  const assignDesignerTeamMutation = useMutation({
    mutationFn: async (variables) => {
      const { data } = await axios.post(
        `${API_URL}/projects/${projectId}/assign-teams`,
        // `http://localhost:4000/projects/${projectId}/assign-teams`,
        variables
      );
      return data;
    },
    onSuccess: (data) => {
      dispatch(setProjectStatus('design-phase'));
      dispatch(setTeamAssigned(true));
      invalidateProjectTasks();
    }
  });

    // Assign designer team and Task mutation
    const assignDesignerTeamTaskMutation = useMutation({
      mutationFn: async (variables) => {
        const { data } = await axios.post(
          `${API_URL}/projects/${projectId}/assign-teams-tasks`,
          // `http://localhost:4000/projects/${projectId}/assign-teams-tasks`,
          variables
        );
        return data;
      },
      onSuccess: (data) => {
        dispatch(setProjectStatus('design-phase'));
        dispatch(setTeamTaskAssigned(true));
        invalidateProjectTasks();
      }
    });

    // Assign Execution Head mutation
  const assignExecutionHeadMutation = useMutation({
    mutationFn: async (variables) => {
      const { data } = await axios.post(
        `${API_URL}/projects/${projectId}/assign-execution-head`,
        // `http://localhost:4000/projects/${projectId}/assign-execution-head`,
        variables
      );
      return data;
    },
    onSuccess: (data) => {
      dispatch(setProjectStatus('execution-phase'));
      if (data.hasTeamAssigned !== undefined) {
        dispatch(setTeamAssigned(data.hasTeamAssigned));
      }
      invalidateProjectTasks();
    }
  });

   // Assign Execution team and Task mutation
    const assignExecutionTeamTaskMutation = useMutation({
      mutationFn: async (variables) => {
        const { data } = await axios.post(
          `${API_URL}/projects/${projectId}/assign-execution-teams-tasks`,
          // `http://localhost:4000/projects/${projectId}/assign-execution-teams-tasks`,
          variables
        );
        return data;
      },
      onSuccess: (data) => {
        dispatch(setProjectStatus('design-phase'));
        dispatch(setTeamTaskAssigned(true));
        invalidateProjectTasks();
      }
    });

    // Start subtask mutation
const startSubtaskMutation = useMutation({
  mutationFn: async (variables) => {
    const { data } = await axios.post(
      // `http://localhost:4000/projects/${projectId}/tasks/${variables.taskId}/subtasks/${variables.subtaskId}/start`,
      `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/subtasks/${variables.subtaskId}/start`,
      {
        username: variables.username,
        timestamp: new Date().toISOString()
      }
    );
    return data;
  },
  onSuccess: (data, variables) => {
    // Update local state if needed
    invalidateProjectTasks();
  }
});

// Update the addSubtask mutation to include assignment
const addSubtaskMutation = useMutation({
  mutationFn: async (variables) => {
    const { data } = await axios.put(
      // `http://localhost:4000/projects/${projectId}/tasks/${variables.taskId}/subtasks`,
      `${API_URL}/projects/${projectId}/tasks/${variables.taskId}/subtasks`,
      {
        name: variables.name,
        username: variables.username,
        assignedTo: variables.assignedTo,
        assignedToName: variables.assignedToName
      }
    );
    return data;
  },
  onSettled: invalidateProjectTasks
});

  const mutations = {
    startTask: startTaskMutation,
    updateTask: updateTaskMutation,
    completeTask: completeTaskMutation,
    updateSubtask: updateSubtaskMutation,
    addSubtask: addSubtaskMutation,
     startSubtask: startSubtaskMutation, // Add this
    deleteSubtask: deleteSubtaskMutation,
    assignDesigner: assignDesignerMutation,
    assignDesignerTeam: assignDesignerTeamMutation,
    assignDesignerTeamTask: assignDesignerTeamTaskMutation,
    assignExecutionHead: assignExecutionHeadMutation,
    assignExecutionTeamTask: assignExecutionTeamTaskMutation
  };

  return {
    ...query,
    startTask: startTaskMutation.mutate,
    updateTask: updateTaskMutation.mutate,
    completeTask: completeTaskMutation.mutate,
    updateSubtask: updateSubtaskMutation.mutate,
    addSubtask: addSubtaskMutation.mutate,
     startSubtask: startSubtaskMutation.mutate, // Add this
    deleteSubtask: deleteSubtaskMutation.mutate,
    assignDesigner: assignDesignerMutation.mutate,
    assignDesignerTeam: assignDesignerTeamMutation.mutate,
    assignDesignerTeamTask: assignDesignerTeamTaskMutation.mutate,
    assignExecutionHead: assignExecutionHeadMutation.mutate,
    assignExecutionTeamTask: assignExecutionTeamTaskMutation.mutate,
    isLoading: query.isLoading || Object.values(mutations).some(m => m.isLoading),
    mutations
  };
};